import { useEffect, useRef } from "react";

import {
  faExclamationCircle,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { AxiosError } from "axios";
import { times } from "lodash-es";

import { Divider } from "src/base-components/Divider";
import { CaseCommentEditor } from "src/cases/caseView/CaseCommentEditor";
import { ActionTriggeredGroup } from "src/cases/caseView/history/events/ActionTriggeredGroup";
import { CaseAssigned } from "src/cases/caseView/history/events/CaseAssigned";
import { CaseCreated } from "src/cases/caseView/history/events/CaseCreated";
import { CaseCreationFlowInitiatedGroup } from "src/cases/caseView/history/events/CaseCreationFlowInitiatedGroup";
import { CaseUnassigned } from "src/cases/caseView/history/events/CaseUnassigned";
import { CaseUpdated } from "src/cases/caseView/history/events/CaseUpdated";
import { CommentAdded } from "src/cases/caseView/history/events/CommentAdded";
import { FileCategoriesUpdated } from "src/cases/caseView/history/events/FileCategoriesUpdated";
import { PinnedEntitiesUpdatedGroup } from "src/cases/caseView/history/events/PinnedEntitiesUpdatedGroup";
import { PinnedEventsUpdatedGroup } from "src/cases/caseView/history/events/PinnedEventsUpdatedGroup";
import { TaskCompleted } from "src/cases/caseView/history/events/TaskCompleted";
import { TaskUncompleted } from "src/cases/caseView/history/events/TaskUncompleted";
import { groupHistoryEvents } from "src/cases/caseView/history/groupHistoryEvents";
import { HistoryItemLoading } from "src/cases/caseView/history/HistoryItemFrame";
import { CaseHistoryEvent, HistoryEntry } from "src/cases/queries/casesTypes";
import { useCaseHistory } from "src/cases/queries/history";
import { EmptyState } from "src/design-system/EmptyState";
import { useOnScreen } from "src/hooks/useOnScreen";
import { CasePageParams } from "src/router/urls";
import { parseApiError } from "src/utils/errors";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseHistory: React.FC = () => {
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCaseHistory({
    caseId,
  });

  const bottomRef = useRef<HTMLDivElement>(null);
  const isBottomVisible = useOnScreen(bottomRef);

  const entries = groupHistoryEvents(data ?? []);

  // Trigger fetchNextPage when bottom sentinel is visible
  useEffect(() => {
    if (isBottomVisible && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isBottomVisible, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex h-full flex-col gap-y-3 p-4 pt-3">
      <CaseCommentEditor />
      <Divider />
      <div className="decideScrollbar flex flex-1 flex-col gap-y-3">
        {isLoading
          ? times(4, (n) => <HistoryItemLoading key={n} isLast={n === 3} />)
          : entries.map((entry, index, array) => {
              return (
                <HistoryEntryRouter
                  key={entry.id}
                  entry={entry}
                  isLast={index === array.length - 1 && !isFetchingNextPage}
                />
              );
            })}
        {isFetchingNextPage && <HistoryItemLoading isLast={false} />}
        {error && <CaseHistoryLoadingError error={error} refetch={refetch} />}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

const CaseHistoryLoadingError: React.FC<{
  error: AxiosError;
  refetch: () => void;
}> = ({ error, refetch }) => {
  const isServerError = error?.response?.status && error.response.status >= 500;
  const errorMessage = parseApiError(error) ?? "Error loading case history";

  return (
    <EmptyState
      actionButton={
        isServerError
          ? {
              icon: faRefresh,
              label: "Retry",
              onClick: () => refetch(),
            }
          : undefined
      }
      description="We're having trouble loading the case history. Please try again later or contact Taktile support if the problem persists."
      headline={errorMessage}
      icon={faExclamationCircle}
      variant="error"
    />
  );
};

const HistoryEntryRouter: React.FC<{
  entry: HistoryEntry;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  // Handle grouped entries
  if (entry.type === "group") {
    switch (entry.event_type) {
      case "case_creation_flow_initiated":
        return <CaseCreationFlowInitiatedGroup entry={entry} isLast={isLast} />;
      case "action_triggered":
        return <ActionTriggeredGroup entry={entry} isLast={isLast} />;
      case "pinned_entities_updated":
        return <PinnedEntitiesUpdatedGroup entry={entry} isLast={isLast} />;
      case "pinned_events_updated":
        return <PinnedEventsUpdatedGroup entry={entry} isLast={isLast} />;
      default:
        assertUnreachable(entry);
        return null;
    }
  }

  // Handle single entries
  const { event_type, event } = entry;
  switch (event_type) {
    case "case_created":
      return (
        <CaseCreated
          entry={event as CaseHistoryEvent<"case_created">}
          isLast={isLast}
        />
      );
    case "case_updated":
      return (
        <CaseUpdated
          entry={event as CaseHistoryEvent<"case_updated">}
          isLast={isLast}
        />
      );
    case "case_assigned":
      return (
        <CaseAssigned
          entry={event as CaseHistoryEvent<"case_assigned">}
          isLast={isLast}
        />
      );
    case "case_unassigned":
      return (
        <CaseUnassigned
          entry={event as CaseHistoryEvent<"case_unassigned">}
          isLast={isLast}
        />
      );
    case "comment_added":
      return (
        <CommentAdded
          entry={event as CaseHistoryEvent<"comment_added">}
          isLast={isLast}
        />
      );
    case "file_categories_updated":
      return (
        <FileCategoriesUpdated
          entry={event as CaseHistoryEvent<"file_categories_updated">}
          isLast={isLast}
        />
      );
    case "task_completed":
      return (
        <TaskCompleted
          entry={event as CaseHistoryEvent<"task_completed">}
          isLast={isLast}
        />
      );
    case "task_uncompleted":
      return (
        <TaskUncompleted
          entry={event as CaseHistoryEvent<"task_uncompleted">}
          isLast={isLast}
        />
      );
    case "case_archived":
    case "block_data_edited":
    case "markdown_block_data_edited":
      return null;
    default:
      assertUnreachable(event_type);
  }
};
